import { useMemo } from 'react'

import {
  matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { FavouriteOrderModel, FavouriteOrderRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['favouriteOrders']

type FavouriteOrderFilters = {
  agency_id?: number,
  provider_price_list_id?: number,
  string?: string,
}

type UseFavouriteOrdersOptions = {
  filters?: FavouriteOrderFilters,
  performHttpRequests?: boolean,
  requestOptions?: FavouriteOrderRequestOptions,
}

function useFavouriteOrders(options: UseFavouriteOrdersOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    string: filterString,
    provider_price_list_id: filterProviderPriceList,
  } = filters

  const {
    updatedEntities: { favouriteOrders: favouriteOrdersUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { favouriteOrders } = useSelector(reduxState => reduxState.entities)

  const filteredFavouriteOrders = useMemo(() => {
    const filtered = Object.values(favouriteOrders).filter((favouriteOrder: FavouriteOrderModel) => {
      const {
        agency_id,
        id,
        provider_price_list_ids = [],
        title,
      } = favouriteOrder

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)
      const matchProviderPriceList = !!matchFilterArrayIncludes(provider_price_list_ids, filterProviderPriceList)

      return matchAgencyId && matchProviderPriceList && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [favouriteOrdersUpdatedAt, JSON.stringify(filters)])

  const filteredFavouriteOrdersCount = filteredFavouriteOrders.length
  const hasFilteredFavouriteOrders = !!filteredFavouriteOrdersCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredFavouriteOrdersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingFavouriteOrders } = useReduxAction(
    'favouriteOrders',
    'loadFavouriteOrders',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredFavouriteOrders,
    filteredFavouriteOrdersCount,
    hasFilteredFavouriteOrders,
    loading: loadingFavouriteOrders,
  }
}

export default useFavouriteOrders
